/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */

import { useMemo } from 'react';

import { getMediaCount } from '@change-corgi/core/features/sapRichMedia';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { Draft } from 'src/app/pages/startAPetitionV2/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentAuthenticatedUser } from 'src/app/shared/hooks/session';
import { googleTagManagerTracking } from 'src/app/shared/utils/googleTagManagerTracking';
import { isFbLoggedIn } from 'src/app/shared/utils/loginOrJoin';

import type { PublishedPetitionResponse } from './api/createPublishedPetition/types';
import { compareNormalizedStrings } from './utils';

type TrackPrePublishActivatePetitionArgs = { petition: PublishedPetitionResponse; draft: Draft | undefined | null };
type TrackConversionArgs = { petition: PublishedPetitionResponse };

type UsePrePublishTrackingReturn = {
	trackPrePublishActivatePetition: (args: TrackPrePublishActivatePetitionArgs) => void;
	trackConversion: (args: TrackConversionArgs) => void;
};

export function usePrePublishTracking(): UsePrePublishTrackingReturn {
	const track = useTracking();
	const sourceLocation = useQueryParamValue('source_location');
	const user = useCurrentAuthenticatedUser();
	const utilityContext = useUtilityContext();

	return useMemo(
		() => ({
			trackPrePublishActivatePetition: ({ petition, draft }) => {
				const mediaCount = getMediaCount(petition.description ?? '');
				const totalMediaCount = Object.values(mediaCount).reduce<number>((sum, count) => sum + count, 0);
				const qualityScore = petition.qualityScore
					? {
							total_score: petition.qualityScore.totalScore,
							categories: petition.qualityScore.categories.map((category) => ({
								name: category.name,
								score: category.score,
								justification: category.justification,
								petition_quality_score_id: category.petitionQualityScoreId,
							})),
						}
					: null;

				const bodyImages = mediaCount.image;
				const bodyYoutube = mediaCount.youtube;

				void track('activate_petition', {
					// user input
					issue_prompt: draft?.input?.issuePrompt,
					personal_story: draft?.input?.personalStory,
					// petition data
					petition_id: `petition_${petition.id}`,
					organization_id: petition.organizationId ? Number(petition.organizationId) : undefined,
					ask_length: petition.ask?.length ?? 0,
					description_length: petition.description?.length ?? 0,
					geo_reach: petition.geoReach ?? undefined,
					relevant_location_localized_name: petition.relevantLocationLocalizedName ?? undefined,
					quality_score: qualityScore,
					media_type: petition.photoId ? 'photo' : 'none',
					image_type: draft?.petitionDraft?.image?.type,
					body_media_count: totalMediaCount,
					body_images: bodyImages,
					body_youtube: bodyYoutube,
					// user data
					fb_logged_in: isFbLoggedIn(utilityContext.facebook.authStatus),
					has_set_password: !!user?.passwordSet,
					login_or_join_method: draft?.metadata?.user?.loginOrJoinMethod,
					starting_sap_user_state: draft?.metadata?.user?.initialLoginState,
					// additional data
					sap_flow: 'sap_3',
					source_location: sourceLocation ?? undefined,
					view_context: 'prepublish',
					ai_title_edited: compareNormalizedStrings(petition.aiAsk, petition.ask || ''),
					ai_description_edited: compareNormalizedStrings(petition.aiDescription, petition.description || ''),
				});
			},
			trackConversion: ({ petition }) => {
				googleTagManagerTracking.push({ petition_id: petition.id, petition_title: petition.ask });
				googleTagManagerTracking.pushUserEmail(user?.email ?? '');
				googleTagManagerTracking.pushConversion('petition_publish');
			},
		}),
		[sourceLocation, track, user?.email, user?.passwordSet, utilityContext.facebook.authStatus],
	);
}
